import { z } from 'zod';
import { FILE_LIMITS, SIGNATURE_PROVIDERS } from '../constants/index.js';

export const createContractSchema = z.object({
  title: z.string().min(2).max(200),
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(FILE_LIMITS.ALLOWED_CONTRACT_MIME_TYPES),
  fileSize: z
    .number()
    .int()
    .positive()
    .max(FILE_LIMITS.MAX_CONTRACT_SIZE_BYTES, 'Arquivo excede 20MB'),
});

export const uploadContractVersionSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(FILE_LIMITS.ALLOWED_CONTRACT_MIME_TYPES),
  fileSize: z.number().int().positive().max(FILE_LIMITS.MAX_CONTRACT_SIZE_BYTES),
  notes: z.string().max(2000).optional(),
});

/**
 * Signature request — owner sends a contract to one or more signers via an
 * external provider (Clicksign by default). Schema covers the call to
 * /contracts/{id}/signature-request. Provider integration handled by
 * lib/clicksign.ts on the backend. When the provider env vars aren't set,
 * the endpoint returns 501 with instructions instead of failing silently.
 */
export const signerSchema = z.object({
  name: z.string().min(2).max(200),
  email: z.string().email('Email do signatário inválido'),
  role: z.enum(['party', 'witness', 'intervenient']).default('party'),
  // CPF helps the provider build a stronger audit trail (Clicksign requires it
  // for the "biometric" tier). Optional — provider falls back to email-only.
  cpf: z.string().regex(/^\d{11}$/).optional(),
});

export const createSignatureRequestSchema = z.object({
  signers: z.array(signerSchema).min(1, 'Adicione ao menos um signatário').max(15),
  provider: z.enum(SIGNATURE_PROVIDERS).optional(), // default clicksign
  deadlineDays: z.number().int().min(1).max(30).optional(),
  message: z.string().max(500).optional(),
});

export type CreateContractInput = z.infer<typeof createContractSchema>;
export type UploadContractVersionInput = z.infer<typeof uploadContractVersionSchema>;
export type Signer = z.infer<typeof signerSchema>;
export type CreateSignatureRequestInput = z.infer<typeof createSignatureRequestSchema>;
